import {
  CalendarDateRangeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  LockClosedIcon,
} from "@heroicons/react/20/solid";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import {
  currencyFormatter,
  InvoiceListItem,
  toDecimal,
} from "shared";
import { Tooltip } from "@mui/material";
import { KeyboardEvent } from "react";
import { useNavigate } from "react-router";

import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Pill } from "../../../common/Atoms/Pill";
import { classNames } from "../../../common/lib/classNames";

interface InvoiceKanbanCardProps {
  invoiceListItem: InvoiceListItem;
  organisationId: string;
  accentColor?: string;
}

export function InvoiceKanbanCard({
  invoiceListItem,
  organisationId,
  accentColor,
}: InvoiceKanbanCardProps) {
  const navigate = useNavigate();

  const dueAmount = currencyFormatter(invoiceListItem.invoice.currency)(toDecimal(invoiceListItem.invoice.totals.dueInCents));
  const daysOverdue = invoiceListItem.invoice.daysOverdue ?? 0;
  const daysUntilDue = Math.abs(daysOverdue);
  const overdueText = daysOverdue > 0
    ? `${daysOverdue}d overdue`
    : daysOverdue === 0
      ? `Due today`
      : `${daysUntilDue}d until due`;
  const cardAriaLabel = `${invoiceListItem.invoice.number || `No Number`}, ${invoiceListItem.contact.name}, ${dueAmount}, ${overdueText}`;
  const overdueChipStyle = daysOverdue > 0 && accentColor ? { backgroundColor: `${accentColor}1A`, color: accentColor } : undefined;
  const showStatusRow = invoiceListItem.invoice.hasPaymentPlan || invoiceListItem.invoice.hasPaymentPromise || invoiceListItem.invoice.hasOpenQuery || invoiceListItem.invoice.externalSentToCustomerFlag;

  function goToInvoice() {
    navigate(`/${organisationId}/invoices/${invoiceListItem.invoice.id}`);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === `Enter` || event.key === ` `) {
      event.preventDefault();
      goToInvoice();
    }
  }

  return (
    <div
      role={ `button` }
      aria-label={ cardAriaLabel }
      tabIndex={ 0 }
      onClick={ goToInvoice }
      onKeyDown={ onKeyDown }
      className={ classNames(
        `cursor-pointer rounded-lg border border-slate-200 bg-white p-4 shadow-sm`,
        `transition-all duration-200 motion-reduce:transform-none motion-reduce:transition-none`,
        `hover:-translate-y-0.5 hover:shadow-md`,
        `focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-500`,
      ) }
    >
      <div className={ `flex items-start justify-between gap-2` }>
        <div className={ `min-w-0` }>
          <div className={ `flex min-w-0 items-center gap-1.5` }>
            { invoiceListItem.invoice.frozen ? (
              <Tooltip title={ `Invoice is frozen. No policies will apply.` }>
                <span className={ `flex shrink-0` }>
                  <LockClosedIcon className={ `h-4 w-4 text-blue-400` } />
                </span>
              </Tooltip>
            ) : null }
            { invoiceListItem.invoice.createdByUs ? (
              <Tooltip title={ `Invoice was created by Paidnice as a late fee or interest charge` }>
                <span className={ `flex shrink-0` }>
                  <CurrencyDollarIcon className={ `h-4 w-4 text-orange-400` } />
                </span>
              </Tooltip>
            ) : null }
            <Paragraph
              className={ `truncate text-slate-900` }
            >
              { invoiceListItem.invoice.number || `No Number` }
            </Paragraph>
          </div>
          <Paragraph
            variant={ `secondary` }
            className={ `truncate text-slate-600` }
          >
            { invoiceListItem.contact.name }
          </Paragraph>
        </div>
        <Pill
          className={ `shrink-0` }
          color={ daysOverdue > 0 ? `red` : `green` }
          message={ overdueText }
          style={ overdueChipStyle }
        />
      </div>

      <div className={ `mt-3 flex items-center justify-between gap-2` }>
        <Paragraph
          className={ `text-slate-900` }
        >
          { dueAmount }
        </Paragraph>
        <Paragraph
          variant={ `secondary` }
          className={ `text-slate-600` }
        >
          { `Due ${invoiceListItem.invoice.dueDate || `-`}` }
        </Paragraph>
      </div>

      { showStatusRow ? (
        <div className={ `mt-3 flex items-center gap-3` }>
          { invoiceListItem.invoice.hasPaymentPlan ? (
            <Tooltip title={ `Payment plan` }>
              <span className={ `flex` }>
                <CalendarDateRangeIcon className={ `h-4 w-4 text-blue-500` } />
              </span>
            </Tooltip>
          ) : null }
          { invoiceListItem.invoice.hasPaymentPromise ? (
            <Tooltip title={ `Payment promise` }>
              <span className={ `flex` }>
                <HandRaisedIcon className={ `h-4 w-4 text-blue-500` } />
              </span>
            </Tooltip>
          ) : null }
          { invoiceListItem.invoice.hasOpenQuery ? (
            <Tooltip title={ `Open query` }>
              <span className={ `flex` }>
                <ExclamationTriangleIcon className={ `h-4 w-4 text-orange-500` } />
              </span>
            </Tooltip>
          ) : null }
          { invoiceListItem.invoice.externalSentToCustomerFlag ? (
            <Tooltip title={ `Sent` }>
              <span className={ `flex` }>
                <EnvelopeIcon className={ `h-4 w-4 text-emerald-500` } />
              </span>
            </Tooltip>
          ) : null }
        </div>
      ) : null }
    </div>
  );
}
